<template>
  <AppReloadPage v-if="isNativePlatform" />
  <component :is="layout">
    <router-view />
  </component>
  <AppAsyncNotification />
  <AppModalFactory />
  <AppPreloader v-if="!isAppInitialized" />
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  provide,
  watch,
  watchEffect,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useLocalStorage, useTitle } from '@vueuse/core'
import { Capacitor } from '@capacitor/core'

import { LinkedDataConnectors } from './views/LinkedData/utils/enums'

import {
  BLUR_DIGITAL_VALUES,
  BLUR_ASSET_NAMES,
  ENV_SHORT_NAMES,
  OWNER_MODE,
  POWER_MODE,
  READONLY_MODE,
  READWRITE_MODE,
  ROUTE_NAME,
  SET_CURRENT_REPOSITORY,
  IS_DISRUPTION_MESSAGE_VISIBLE,
  ADVISOR_MODE,
} from '@/const'

import AdvisorLayout from '@/layouts/Main/Advisor.vue'
import DefaultLayout from '@/layouts/Default.vue'
import EmptyLayout from '@/layouts/Empty.vue'
import MainLayout from '@/layouts/Main/Main.vue'

import { useLinkedDataStore } from './store/linkedData'
import { useMainStore } from './store/main'
import { useRepositoriesStore } from './store/repositories/index'
import { useSettingsStore } from './store/settings'
import { useUserSettingsStore } from './store/user/settings'

import AppAsyncNotification from '@/plugins/notification/NotificationGroup.vue'
import { useCapacitor } from '@/plugins/capacitor'
import { useNotifications } from './plugins/notification'

import { handleCatchedError } from './helpers/common'

import { useAlertsService } from '@/services/alerts'

import { AppModalFactory, AppPreloader, AppReloadPage } from '@app'

defineOptions({
  name: 'App',
  components: {
    AdvisorLayout,
    DefaultLayout,
    MainLayout,
    EmptyLayout,
  },
})

const linkedDataStore = useLinkedDataStore()
const mainStore = useMainStore()
const repositoriesStore = useRepositoriesStore()
const settingsStore = useSettingsStore()
const userSettingsStore = useUserSettingsStore()

const capacitor = useCapacitor()

const { error } = useNotifications()

useAlertsService()

const route = useRoute()
const router = useRouter()

const blurDigitalValues = useLocalStorage(BLUR_DIGITAL_VALUES, false)
const blurAssetNames = useLocalStorage(BLUR_ASSET_NAMES, false)

provide(IS_DISRUPTION_MESSAGE_VISIBLE, false)

const isNativePlatform = computed(() => Capacitor.isNativePlatform())
const isAppInitialized = computed(() => mainStore.getAppInitialized)
const isOwner = computed(() => repositoriesStore.isOwner)
const isReadonly = computed(() => repositoriesStore.isReadonly)
const isReadWrite = computed(() => repositoriesStore.isReadWrite)
const repositoryId = computed(() => repositoriesStore.getCurrentId)
const repositoryName = computed(() => repositoriesStore.getCurrentName)
const currency = computed(() => repositoriesStore.getCurrentCurrencySymbol)
provide('repositoryId', repositoryId)
provide('currency', currency)

const isPowerMode = computed(() => settingsStore.getPowerMode)
provide(POWER_MODE, isPowerMode)

const isAdvisorMode = computed(() => userSettingsStore.getAdvisorMode)
provide(ADVISOR_MODE, isAdvisorMode)

const title = computed(() => {
  const envString = __IS_PROD__
    ? ''
    : `[${ENV_SHORT_NAMES[__APP_MODE__] || '??'}] `
  const repoString = repositoryName.value ? `${repositoryName.value} | ` : ''
  const titleString = route.meta.title || 'Allposit Inc.'
  return `${envString}${repoString}${titleString}`
})
useTitle(title)

provide(OWNER_MODE, isOwner)
provide(READONLY_MODE, isReadonly)
provide(READWRITE_MODE, isReadWrite)

const setCurrentRepository = async (id?: string) => {
  await repositoriesStore.setCurrentId(id)
}
provide(SET_CURRENT_REPOSITORY, setCurrentRepository)

const layout = computed(() => route?.meta?.layout || 'DefaultLayout')

watch(
  blurDigitalValues,
  value => {
    if (value) {
      document.body.classList.add('blur-numbers')
    } else {
      document.body.classList.remove('blur-numbers')
    }
  },
  { immediate: true },
)

watch(
  blurAssetNames,
  value => {
    if (value) {
      document.body.classList.add('blur-asset-names')
    } else {
      document.body.classList.remove('blur-asset-names')
    }
  },
  { immediate: true },
)

watchEffect(() => {
  // global flags for router, menu etc
  // @ts-expect-error - window is global
  window[OWNER_MODE] = isOwner.value
  // @ts-expect-error - window is global
  window[READONLY_MODE] = isReadonly.value
  // @ts-expect-error - window is global
  window[READWRITE_MODE] = isReadWrite.value
})

watchEffect(async () => {
  const CONNECTORS_LIST = Object.values(LinkedDataConnectors).map(item =>
    item.toLowerCase(),
  )
  if (route?.name !== ROUTE_NAME.LINKED_DATA || !repositoryId.value) return
  const errorDescription = route?.query.error_description?.toString()
  if (errorDescription) {
    await error({
      message: errorDescription,
    })
    router.push({ name: ROUTE_NAME.LINKED_DATA })
    return
  }
  const state = route?.query.state?.toString()
  const code = route?.query.code?.toString()
  if (!state || !code) return
  const [providerName, connectorName] = state.split(',')
  if (providerName === undefined || !CONNECTORS_LIST.includes(providerName))
    return
  try {
    await linkedDataStore.callback(code, providerName, connectorName)
  } catch (e) {
    handleCatchedError(e as string)
  }
  router.push({ name: ROUTE_NAME.LINKED_DATA })
})

onBeforeUnmount(() => {
  // @ts-expect-error - window is global
  delete window[OWNER_MODE]
  // @ts-expect-error - window is global
  delete window[READONLY_MODE]
  // @ts-expect-error - window is global
  delete window[READWRITE_MODE]
})

onMounted(() => {
  capacitor?.init()
})
</script>

<style>
.number-node {
  @apply tabular-nums;
}
.blur-numbers {
  .number-node {
    @apply blur-sm hover:blur-none;

    &.card-balance__amount {
      @apply blur hover:blur-none;
    }
  }
}
.blur-asset-names {
  .asset-node {
    @apply blur-sm hover:blur-none;
  }
}
</style>
